import { ChangeEvent, FormEvent, useCallback, useState } from 'react';

import { rem, FormControl, FormLabel, Input, Button, Alert, AlertDescription } from '@vimeo/bokeh';
import { createInteractionDomName } from '@vimeo/cloud-live/libs/shared/utils/dom/identifying';
import { translations } from '@vimeo/cloud-live/translations';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { browserConfig } from '@vimeo/live-core/configs/BrowserConfig';
import { interactionToolsConfig } from '@vimeo/live-core/configs/InteractionToolsConfig';
import { EInteractionAuthOption } from '@vimeo/live-core/types/interaction_room';

import { useInteractionLoginOption } from '../shared/useInteractionLoginOption';

/**
 * Anonymous login with custom nickname.
 */
export function AnonymousLogin({
  id = createInteractionDomName('anonymous-login'),
}: IBaseDomElementProps) {
  const [roomDisplayName, setRoomDisplayName] = useState('');
  const validateRoomDisplayName: string = roomDisplayName.trim();

  const isValidDisplayName: boolean =
    validateRoomDisplayName.length >= interactionToolsConfig.AUTHORIZATION.MIN_DISPLAYNAME_LENGTH &&
    validateRoomDisplayName.length <= interactionToolsConfig.AUTHORIZATION.MAX_DISPLAYNAME_LENGTH;

  const { isLoading, isLoginFailed, error, errorMessage, onResetError, onLogin } =
    useInteractionLoginOption(EInteractionAuthOption.ANONYMOUS);

  const onRoomDisplayNameChanged = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setRoomDisplayName(event.target.value);

      if (error) {
        onResetError();
      }
    },
    [error, onResetError],
  );

  const onAuthSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      event.stopPropagation();

      onLogin({ roomDisplayName: validateRoomDisplayName });
    },
    [validateRoomDisplayName, onLogin],
  );

  return (
    <form onSubmit={onAuthSubmit} id={id}>
      <FormControl isInvalid={isLoginFailed}>
        <FormLabel color={'text-primary'} fontWeight={700} marginBottom={rem(8)}>
          {translations.name}
        </FormLabel>
        <Input
          id={createInteractionDomName(id, 'input')}
          size={'md'}
          value={roomDisplayName}
          isDisabled={isLoading}
          fontSize={rem(browserConfig.BROWSER?.isMobile ? 16 : 14)}
          onChange={onRoomDisplayNameChanged}
        />
      </FormControl>

      <Button
        id={createInteractionDomName(id, 'button')}
        size={'md'}
        type={'submit'}
        isDisabled={isLoading || !isValidDisplayName}
        width={'100%'}
        marginTop={rem(24)}
      >
        {translations.joinAudience}
      </Button>

      {errorMessage ? (
        <Alert status={'error'} margin={`${rem(8)} 0 0 0`} onClose={onResetError}>
          <AlertDescription margin={0}>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}
    </form>
  );
}
